/* eslint-disable max-classes-per-file*/
import { PLATFORM } from 'aurelia-framework';

import { DrilldownCommonModel } from '~/components/dialog/dialog-with-tabs/drilldown-model';
import { Tab } from '~/components/dialog/dialog-with-tabs/tab-model';
// tslint:disable-next-line no-circular-imports
import { ATOInterfaces } from '~/forms/ato-interfaces/ato-interfaces';
import { DataCompareTab } from '~/forms/ato-interfaces/drilldown-dialog/ato/data-compare-tab';
import { RawDataTab } from '~/forms/ato-interfaces/drilldown-dialog/ato/raw-data-tab';
import { SummaryTab } from '~/forms/ato-interfaces/drilldown-dialog/ato/summary-tab';
import { ATOInterfacesModel } from '~/forms/ato-interfaces/models/ato-interfaces-model';
import { ATORequest } from '~/forms/ato-interfaces/models/ato-request';
import { ColumnDefinitionMap, Entity } from '~/models/column-definition';

// tslint:disable:max-classes-per-file
class Mapping {
	public label: string;
	public resource: string;
}

/**
 * Map the response type to the displayed tabs
 */
export class TemplateMapping {
	// common tabs. These must be defined before use
	public static readonly rawDataTab = {
		label: 'ato-interfaces:raw-data',
		resource: PLATFORM.moduleName(
			'forms/ato-interfaces/drilldown-dialog/raw-data-tab.html',
		),
	};

	// Common ATO tab (includes MAAS/MATS/SMSF)
	public static readonly atoSummaryTab = {
		label: 'ato-interfaces:summary',
		resource: PLATFORM.moduleName(
			'forms/ato-interfaces/drilldown-dialog/ato/summary-tab.html',
		),
	};
	public static readonly atoDataCompareTab = {
		label: 'ato-interfaces:data-compare',
		resource: PLATFORM.moduleName(
			'forms/ato-interfaces/drilldown-dialog/ato/data-compare-tab.html',
		),
	};
	public static readonly atoRawDataTab = {
		label: 'ato-interfaces:raw-data',
		resource: PLATFORM.moduleName(
			'forms/ato-interfaces/drilldown-dialog/ato/raw-data-tab.html',
		),
	};

	// drilldown type -> list of tab mappings
	public static readonly mappings = {
		stic: [
			{
				label: 'ato-interfaces:response-summary',
				resource: PLATFORM.moduleName(
					'forms/ato-interfaces/drilldown-dialog/stic/stic-response-tab.html',
				),
			},
			TemplateMapping.rawDataTab,
		],
		smat: [
			{
				label: 'ato-interfaces:response-summary',
				resource: PLATFORM.moduleName(
					'forms/ato-interfaces/drilldown-dialog/smat/smat-response-tab.html',
				),
			},
			{
				label: 'ato-interfaces:matched-funds',
				resource: PLATFORM.moduleName(
					'forms/ato-interfaces/drilldown-dialog/smat/matched-funds-tab.html',
				),
			},
			TemplateMapping.rawDataTab,
		],
		mats: [TemplateMapping.atoSummaryTab, TemplateMapping.atoRawDataTab],
		maas: [
			TemplateMapping.atoSummaryTab,
			TemplateMapping.atoDataCompareTab,
			TemplateMapping.atoRawDataTab,
		],
		smsfvrfy: [
			TemplateMapping.atoSummaryTab,
			TemplateMapping.atoRawDataTab,
		],
	};

	public static getAsListOfNewTabs(
		dataModelReference: ATOInterfacesModel,
		tableColumnDefinitions: ColumnDefinitionMap,
		form?: ATOInterfaces,
	): Tab[] {
		const requestType = (<Entity<ATORequest>>(
			dataModelReference.atoRequest
		)).TQv_Type.toString()
			.toLowerCase()
			.replace(/^mats-.*/, 'mats')
			.replace(/^maas-.*/, 'maas');
		const mappingList = this.mappings[requestType];

		if (!mappingList) {
			console.error('Unknown ATO Request type: ' + requestType);
			return [];
		}

		// when user is not allowed to see details of SMAT request, all tabs are empty so tablist not needed
		if (
			dataModelReference.atoRequest.TQv_Type === 'SMAT' &&
			dataModelReference.invalidReasons &&
			dataModelReference.invalidReasons.length > 0
		) {
			return undefined;
		}

		const ret = [];
		for (const index of Object.keys(mappingList)) {
			ret.push(
				this.getAsNewTab(
					mappingList[index],
					dataModelReference,
					tableColumnDefinitions,
					form,
				),
			);
		}

		return ret;
	}

	private static getAsNewTab(
		mapping: Mapping,
		dataModelReference: ATOInterfacesModel,
		tableColumnDefinitions: ColumnDefinitionMap,
		form?: ATOInterfaces,
	): Tab {
		let tabModel: DrilldownCommonModel;
		let tabDisabled: boolean = false;
		switch (mapping) {
			case TemplateMapping.atoSummaryTab:
				tabModel = new SummaryTab(
					mapping.resource,
					dataModelReference,
					tableColumnDefinitions,
				);
				break;
			case TemplateMapping.atoDataCompareTab:
				tabModel = new DataCompareTab(
					mapping.resource,
					dataModelReference,
					tableColumnDefinitions,
					form,
				);
				tabDisabled =
					dataModelReference.invalidReasons &&
					dataModelReference.invalidReasons.length > 0;

				// only enable data compare tab if request status is complete and data exists, is reviewed or result is corrected
				if (
					!(
						dataModelReference.atoRequest.TQz_Status.trim() ===
							'R' &&
						dataModelReference.atoResponse &&
						(dataModelReference.atoResponse.TOz_Data_Exists.trim() ===
							'D' ||
							dataModelReference.atoResponse.TOz_Data_Exists.trim() ===
								'R' ||
							dataModelReference.atoResponse.TOz_Data_Exists.trim() ===
								'E' ||
							dataModelReference.atoResponse.TOz_Result.trim() ===
								'C')
					)
				) {
					tabDisabled = true;
				}
				break;
			case TemplateMapping.atoRawDataTab:
				tabModel = new RawDataTab(
					mapping.resource,
					dataModelReference,
					tableColumnDefinitions,
				);
				tabDisabled =
					dataModelReference.invalidReasons &&
					dataModelReference.invalidReasons.length > 0;
				break;
			case TemplateMapping.rawDataTab:
				tabModel = new DrilldownCommonModel(
					mapping.resource,
					dataModelReference,
					tableColumnDefinitions,
				);
				tabDisabled =
					dataModelReference.invalidReasons &&
					dataModelReference.invalidReasons.length > 0;
				break;
			default:
				tabModel = new DrilldownCommonModel(
					mapping.resource,
					dataModelReference,
					tableColumnDefinitions,
				);
				break;
		}

		const newTab = {
			label: mapping.label,
			model: tabModel,
			disabled: tabDisabled,
		};
		return newTab;
	}
}
